import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { DataNumber } from '../components/data/data-text';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';

export const Route = createFileRoute('/admin/ai')({ component: AiRoutingPage });

type Mode = 'claude' | 'openai' | 'native' | 'compare';
type RouteKey = 'anthropic' | 'openai' | 'native';
type Effort = 'low' | 'medium' | 'high';
type Thinking = 'on' | 'off';
interface Reasoning {
  effort?: Effort;
  thinking?: Thinking;
}
interface Settings {
  routing: { mode: Mode; overrides?: Partial<Record<string, Mode>>; compareRoutes?: RouteKey[] };
  models: Partial<Record<string, Partial<Record<RouteKey, string>>>>;
  reasoning?: Partial<Record<string, Reasoning>>;
  budgets: { globalMonthlyUsd: number | null; perUserMonthlyUsd: number | null };
}
interface AiAdmin {
  settings: Settings;
  defaults: { models: Record<string, Record<RouteKey, string>>; reasoning?: Partial<Record<string, Reasoning>> };
  tasks: { key: string; switchable: boolean }[];
  connected: boolean;
  usage: {
    since: number;
    totalUsd: number;
    calls: number;
    byRoute: { route: RouteKey; calls: number; failed: number; fallbacks: number; costUsd: number }[];
    byUser: { userId: string | null; email: string | null; name: string | null; calls: number; costUsd: number }[];
  };
}
interface TestResult {
  ok: boolean;
  route: RouteKey;
  model: string | null;
  isFallback: boolean;
  error: string | null;
  latencyMs: number;
}

const MODES: { key: Mode; label: string; help: string }[] = [
  { key: 'claude', label: 'Claude', help: 'Anthropic models (default). Falls back to OpenAI, then Native, if Claude is down.' },
  { key: 'openai', label: 'OpenAI', help: 'OpenAI models for the same tasks. Falls back to Claude, then Native.' },
  { key: 'native', label: 'Native', help: 'Cloudflare Workers AI only: the same models used for confidential content. No fallback.' },
  { key: 'compare', label: 'Compare', help: 'Runs 2 or 3 routes side by side; people adopt the better result. Multiplies cost and time.' },
];
const ROUTES: { key: RouteKey; label: string }[] = [
  { key: 'anthropic', label: 'Claude' },
  { key: 'openai', label: 'OpenAI' },
  { key: 'native', label: 'Native' },
];
const routeLabel = (r: RouteKey) => ROUTES.find(x => x.key === r)?.label ?? r;
const modeLabel = (m: Mode) => MODES.find(x => x.key === m)?.label ?? m;
const TASKS: Record<string, { label: string; help: string }> = {
  ocr: { label: 'OCR & images', help: 'Reading scans and photos.' },
  extraction: { label: 'Extraction', help: 'Entities, claims, relations and events from documents.' },
  resolution: { label: 'Entity resolution', help: 'Tie-breaks when a name could match several entities.' },
  recheck: { label: 'AI re-check', help: 'Review step 3: checks accepted facts against the source.' },
  chat: { label: 'Chat', help: 'Answers in Chat, with citations.' },
  findings: { label: 'AI findings', help: 'Findings in reports.' },
  cluster_label: { label: 'Cluster labels', help: 'Names for clusters in the graph.' },
  column_mapping: { label: 'Column mapping', help: 'Suggestions when importing spreadsheets.' },
  report_query: { label: 'Report questions', help: 'Ask in words: a typed request becomes a report question.' },
  findings_translate: { label: 'Findings translation', help: 'Translates AI findings between English and French, keeping every citation.' },
  report_plan: { label: 'Report plans', help: 'A described report becomes a plan: question, sections, figures.' },
  cross_check: { label: 'Interview cross-check', help: 'Says whether other documents state what an interview alleges.' },
  translation: { label: 'Translation', help: 'Translations of statements and names.' },
  embedding: { label: 'Embeddings', help: 'Search vectors. One fixed model so results stay comparable.' },
  transcription: { label: 'Transcription', help: 'Audio and video transcripts. One fixed model.' },
};
const EFFORTS: { key: Effort; label: string }[] = [
  { key: 'low', label: 'Low' },
  { key: 'medium', label: 'Medium' },
  { key: 'high', label: 'High' },
];
const THINKING: { key: Thinking; label: string }[] = [
  { key: 'on', label: 'On' },
  { key: 'off', label: 'Off' },
];
const effortLabel = (e?: Effort) => EFFORTS.find(x => x.key === e)?.label;
const thinkingLabel = (t?: Thinking) => THINKING.find(x => x.key === t)?.label;
const usd = (n: number) => `$${n.toFixed(n < 10 ? 2 : 0)}`;

function AiRoutingPage() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const q = useQuery({ queryKey: ['admin', 'ai'], queryFn: () => api<AiAdmin>('/admin/ai') });
  const [form, setForm] = useState<Settings | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [tests, setTests] = useState<Partial<Record<RouteKey, TestResult | string>>>({});
  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);

  const save = useMutation({
    mutationFn: (s: Settings) => withReauth(() => api<{ settings: Settings }>('/admin/ai', { method: 'PUT', body: s })),
    onSuccess: r => {
      setForm(r.settings);
      setMessage({ kind: 'ok', text: 'Saved. New AI calls use these settings right away.' });
      qc.invalidateQueries({ queryKey: ['admin', 'ai'] });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
    },
  });
  const test = useMutation({
    mutationFn: (route: RouteKey) => withReauth(() => api<TestResult>('/admin/ai/test', { body: { route } })),
    onSuccess: (r, route) => {
      setTests(t => ({ ...t, [route]: r }));
      qc.invalidateQueries({ queryKey: ['admin', 'ai'] });
    },
    onError: (e, route) => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setTests(t => ({ ...t, [route]: e instanceof ApiError ? e.message : 'Something went wrong.' }));
    },
  });

  if (!q.data || !form) return <PageHeader title="AI routing" description="Loading…" />;
  const data = q.data;
  const usesCompare = form.routing.mode === 'compare' || Object.values(form.routing.overrides ?? {}).includes('compare');
  const compareRoutes = form.routing.compareRoutes ?? [];
  const setRouting = (r: Partial<Settings['routing']>) => setForm({ ...form, routing: { ...form.routing, ...r } });
  const setOverride = (task: string, mode: Mode | '') => {
    const overrides = { ...form.routing.overrides };
    if (mode) overrides[task] = mode;
    else delete overrides[task];
    setRouting({ overrides });
  };
  const setModel = (task: string, route: RouteKey, value: string) =>
    setForm({ ...form, models: { ...form.models, [task]: { ...form.models[task], [route]: value } } });
  const setReasoning = (task: string, patch: Partial<Record<keyof Reasoning, string>>) => {
    const next: Record<string, string | undefined> = { ...form.reasoning?.[task], ...patch };
    const entry: Reasoning = {};
    if (next.effort) entry.effort = next.effort as Effort;
    if (next.thinking) entry.thinking = next.thinking as Thinking;
    const reasoning = { ...form.reasoning };
    if (entry.effort || entry.thinking) reasoning[task] = entry;
    else delete reasoning[task];
    setForm({ ...form, reasoning });
  };
  const setBudget = (key: keyof Settings['budgets'], value: string) =>
    setForm({ ...form, budgets: { ...form.budgets, [key]: value.trim() === '' ? null : Number(value) } });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    save.mutate(form);
  };

  return (
    <form onSubmit={submit} className="space-y-8" aria-label="AI routing settings">
      <PageHeader
        title="AI routing"
        description="Which AI models do each task for standard content, what to do when a provider is down, and monthly budgets."
        actions={
          <Button type="submit" disabled={save.isPending}>
            Save changes
          </Button>
        }
      />
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        {data.connected
          ? 'Connected: every call goes through the Cloudflare AI Gateway "hoopoe".'
          : 'AI is not connected in this environment (local development). Settings still save; calls fail until it runs on staging or production.'}
      </p>
      <Tip id="ai-routing-confidential" learnMore="ai-routing-and-budgets">
        <span className="inline-flex items-center gap-1">
          <Lock className="size-3.5" aria-hidden="true" /> Confidential content
        </span>{' '}
        always stays on Cloudflare's own models, whatever you choose here, including in Compare.
      </Tip>
      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={message.kind === 'error' ? 'text-sm text-danger-foreground' : 'text-sm'}
        >
          {message.text}
        </p>
      )}

      <fieldset className="space-y-3">
        <legend className="mb-2 font-semibold">Routing mode</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {MODES.map(m => (
            <label
              key={m.key}
              className="flex cursor-pointer gap-3 rounded-lg border bg-surface p-3 text-sm has-[:checked]:border-foreground"
            >
              <input
                type="radio"
                name="mode"
                value={m.key}
                checked={form.routing.mode === m.key}
                onChange={() => setRouting({ mode: m.key })}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{m.label}</span>
                <span className="block text-muted-foreground">{m.help}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2" aria-describedby="compare-help">
        <legend className="mb-1 font-semibold">Compare routes</legend>
        <p id="compare-help" className="text-sm text-muted-foreground">
          Used wherever Compare is on (globally or for a task). Pick 2 or 3.
          {usesCompare && compareRoutes.length < 2 && <span className="text-danger-foreground"> Compare needs at least two routes.</span>}
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          {ROUTES.map(r => (
            <label key={r.key} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={compareRoutes.includes(r.key)}
                onChange={e =>
                  setRouting({
                    compareRoutes: e.target.checked ? [...compareRoutes, r.key] : compareRoutes.filter(x => x !== r.key),
                  })
                }
              />
              {r.label}
            </label>
          ))}
        </div>
      </fieldset>

      <section aria-labelledby="tasks-title" className="space-y-3">
        <h2 id="tasks-title" className="font-semibold">
          Per task
        </h2>
        <div className="relative overflow-x-auto rounded-lg border bg-surface">
          <table className="w-full text-sm" aria-label="Route per task">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="p-2 text-start font-medium">Task</th>
                <th className="p-2 text-start font-medium">Route</th>
              </tr>
            </thead>
            <tbody>
              {data.tasks.map(t => {
                const meta = TASKS[t.key] ?? { label: t.key, help: '' };
                return (
                  <tr key={t.key} className="border-b last:border-0">
                    <td className="p-2">
                      <span className="font-medium">{meta.label}</span>
                      <span className="block text-xs text-muted-foreground">{meta.help}</span>
                    </td>
                    <td className="p-2">
                      {t.switchable ? (
                        <label>
                          <span className="sr-only">Route for {meta.label}</span>
                          <select
                            value={form.routing.overrides?.[t.key] ?? ''}
                            onChange={e => setOverride(t.key, e.target.value as Mode | '')}
                            className="h-8 rounded-md border border-input bg-surface px-2"
                          >
                            <option value="">Same as mode ({modeLabel(form.routing.mode)})</option>
                            {MODES.map(m => (
                              <option key={m.key} value={m.key}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <span className="text-muted-foreground">Always Native (fixed)</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="budget-title" className="space-y-3">
        <h2 id="budget-title" className="font-semibold">
          Monthly budgets
        </h2>
        <p className="text-sm text-muted-foreground">
          When a cap is reached, new AI work pauses until the next month or until you raise it. Leave empty for no cap. Search keeps
          working.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <BudgetInput
            label="Whole team, per month (USD)"
            help="All AI calls by everyone, including Compare runs and fallbacks. Resets on the 1st (UTC)."
            value={form.budgets.globalMonthlyUsd}
            onChange={v => setBudget('globalMonthlyUsd', v)}
            spent={data.usage.totalUsd}
          />
          <BudgetInput
            label="Per person, per month (USD)"
            help="Calls made for one person: their uploads, chats and reports."
            value={form.budgets.perUserMonthlyUsd}
            onChange={v => setBudget('perUserMonthlyUsd', v)}
          />
        </div>
      </section>

      <details className="rounded-lg border bg-surface p-3">
        <summary className="cursor-pointer font-semibold">Models</summary>
        <p className="mt-2 text-sm text-muted-foreground">
          Model IDs per task and route. Empty means the default (shown greyed). Claude models start with "anthropic/", OpenAI with
          "openai/", Cloudflare models with "@cf/".
        </p>
        <p id="reasoning-help" className="mt-1 text-sm text-muted-foreground">
          Effort and Thinking apply to Claude only: lower effort or thinking off writes fewer tokens, so the task costs less. The model may
          not support every option; unsupported options are skipped.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" aria-label="Models per task">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="p-2 text-start font-medium">Task</th>
                {ROUTES.map(r => (
                  <th key={r.key} className="p-2 text-start font-medium">
                    {r.label}
                  </th>
                ))}
                <th className="p-2 text-start font-medium">
                  <span className="inline-flex items-center gap-1">
                    Effort
                    <FieldHelp
                      label="Effort"
                      help="How much work Claude puts into an answer. Low is cheaper and faster; Default sends nothing, so the model uses its own default."
                      learnMore="ai-routing-and-budgets"
                    />
                  </span>
                </th>
                <th className="p-2 text-start font-medium">
                  <span className="inline-flex items-center gap-1">
                    Thinking
                    <FieldHelp
                      label="Thinking"
                      help="Claude models think before answering, and thinking is billed like the answer. Off skips it where the model allows."
                      learnMore="ai-routing-and-budgets"
                    />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.tasks.map(t => (
                <tr key={t.key} className="border-b last:border-0">
                  <td className="p-2 font-medium">{TASKS[t.key]?.label ?? t.key}</td>
                  {ROUTES.map(r => (
                    <td key={r.key} className="p-2">
                      {t.switchable || r.key === 'native' ? (
                        <Input
                          aria-label={`${TASKS[t.key]?.label ?? t.key} model on ${r.label}`}
                          value={form.models[t.key]?.[r.key] ?? ''}
                          placeholder={data.defaults.models[t.key]?.[r.key]}
                          onChange={e => setModel(t.key, r.key, e.target.value)}
                          className="h-8 min-w-48 font-mono text-xs"
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  ))}
                  <ReasoningCells
                    label={TASKS[t.key]?.label ?? t.key}
                    switchable={t.switchable}
                    value={form.reasoning?.[t.key] ?? {}}
                    fallback={data.defaults.reasoning?.[t.key] ?? {}}
                    onChange={patch => setReasoning(t.key, patch)}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <section aria-labelledby="usage-title" className="space-y-3">
        <h2 id="usage-title" className="font-semibold">
          This month
        </h2>
        <p className="text-sm text-muted-foreground">
          <DataNumber>{usd(data.usage.totalUsd)}</DataNumber> estimated across <DataNumber>{data.usage.calls}</DataNumber> calls since{' '}
          {new Date(data.usage.since).toISOString().slice(0, 10)}. The AI Gateway dashboard has the billed amounts.
        </p>
        <div className="relative overflow-x-auto rounded-lg border bg-surface">
          <table className="w-full text-sm" aria-label="Usage by route">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="p-2 text-start font-medium">Route</th>
                <th className="p-2 text-end font-medium">Calls</th>
                <th className="p-2 text-end font-medium">Failed</th>
                <th className="p-2 text-end font-medium">Fallbacks</th>
                <th className="p-2 text-end font-medium">Cost</th>
                <th className="p-2 text-start font-medium">Check</th>
              </tr>
            </thead>
            <tbody>
              {ROUTES.map(r => {
                const u = data.usage.byRoute.find(x => x.route === r.key);
                const t = tests[r.key];
                return (
                  <tr key={r.key} className="border-b last:border-0">
                    <td className="p-2 font-medium">{r.label}</td>
                    <td className="p-2 text-end">
                      <DataNumber>{u?.calls ?? 0}</DataNumber>
                    </td>
                    <td className="p-2 text-end">
                      <DataNumber>{u?.failed ?? 0}</DataNumber>
                    </td>
                    <td className="p-2 text-end">
                      <DataNumber>{u?.fallbacks ?? 0}</DataNumber>
                    </td>
                    <td className="p-2 text-end">
                      <DataNumber>{usd(u?.costUsd ?? 0)}</DataNumber>
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => test.mutate(r.key)} disabled={test.isPending}>
                          Test {r.label}
                        </Button>
                        {t && (
                          <span className="text-xs" role="status">
                            {typeof t === 'string'
                              ? t
                              : t.ok
                                ? `Answered by ${routeLabel(t.route)}${t.isFallback ? ' (fallback)' : ''} in ${t.latencyMs} ms`
                                : `Failed: ${t.error}`}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data.usage.byUser.length > 0 && (
          <div className="relative overflow-x-auto rounded-lg border bg-surface">
            <table className="w-full text-sm" aria-label="Spend by person">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="p-2 text-start font-medium">Person</th>
                  <th className="p-2 text-end font-medium">Calls</th>
                  <th className="p-2 text-end font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.usage.byUser.map(u => (
                  <tr key={u.userId ?? 'system'} className="border-b last:border-0">
                    <td className="p-2">{u.userId ? (u.email ?? u.userId) : 'System jobs'}</td>
                    <td className="p-2 text-end">
                      <DataNumber>{u.calls}</DataNumber>
                    </td>
                    <td className="p-2 text-end">
                      <DataNumber>{usd(u.costUsd)}</DataNumber>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </form>
  );
}

/** Effort and Thinking for one task (D407): "Default" is the code default, named when there is one. */
function ReasoningCells({
  label,
  switchable,
  value,
  fallback,
  onChange,
}: {
  label: string;
  switchable: boolean;
  value: Reasoning;
  fallback: Reasoning;
  onChange: (patch: Partial<Record<keyof Reasoning, string>>) => void;
}) {
  if (!switchable)
    return (
      <>
        <td className="p-2 text-muted-foreground">—</td>
        <td className="p-2 text-muted-foreground">—</td>
      </>
    );
  const select = 'h-8 rounded-md border border-input bg-surface px-2';
  const named = (l?: string) => (l ? `Default (${l})` : 'Default');
  return (
    <>
      <td className="p-2">
        <select
          aria-label={`${label} effort`}
          aria-describedby="reasoning-help"
          value={value.effort ?? ''}
          onChange={e => onChange({ effort: e.target.value })}
          className={select}
        >
          <option value="">{named(effortLabel(fallback.effort))}</option>
          {EFFORTS.map(o => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
      <td className="p-2">
        <select
          aria-label={`${label} thinking`}
          aria-describedby="reasoning-help"
          value={value.thinking ?? ''}
          onChange={e => onChange({ thinking: e.target.value })}
          className={select}
        >
          <option value="">{named(thinkingLabel(fallback.thinking))}</option>
          {THINKING.map(o => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
    </>
  );
}

function BudgetInput({
  label,
  help,
  value,
  onChange,
  spent,
}: {
  label: string;
  help: string;
  value: number | null;
  onChange: (v: string) => void;
  spent?: number;
}) {
  const id = useId();
  const pct = spent !== undefined && value ? Math.min(100, Math.round((spent / value) * 100)) : null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        <FieldHelp label={label} help={help} />
      </div>
      <Input
        id={id}
        type="number"
        min={0}
        step={1}
        inputMode="decimal"
        value={value ?? ''}
        placeholder="No cap"
        onChange={e => onChange(e.target.value)}
      />
      {pct !== null && (
        <div className="space-y-1">
          <div
            className="h-1.5 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label={`${label}: used this month`}
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={pct >= 100 ? 'h-full bg-danger' : pct >= 80 ? 'h-full bg-warning' : 'h-full bg-primary'}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            <DataNumber>{pct}%</DataNumber> used this month
          </p>
        </div>
      )}
    </div>
  );
}
